import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, TrendingUp, Calendar, Clock, 
  Loader2, RefreshCw, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const formatCents = (cents) => {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function AdminPlatformRevenue() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);

  const fetchRevenue = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getPlatformRevenue();
      setRevenue(res.data);
    } catch (err) {
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTxLoading(true);
      const res = await adminAPI.getPlatformRevenueTransactions(20);
      setTransactions(res.data.transactions || []);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenue();
    fetchTransactions();
  }, []);

  const handleRefresh = () => {
    fetchRevenue();
    fetchTransactions();
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden" data-testid="platform-revenue-page">
      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Platform Revenue</h1>
            <p className="text-sm text-gray-500">Fee income from Stripe payment processing (0.50%)</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="refresh-revenue-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : revenue ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card data-testid="revenue-today-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Today</p>
                      <p className="text-2xl font-bold text-emerald-600" data-testid="revenue-today-value">
                        {formatCents(revenue.today_cents)}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-emerald-400" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="revenue-month-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">This Month</p>
                      <p className="text-2xl font-bold text-blue-600" data-testid="revenue-month-value">
                        {formatCents(revenue.month_cents)}
                      </p>
                    </div>
                    <Calendar className="h-8 w-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="revenue-lifetime-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Lifetime</p>
                      <p className="text-2xl font-bold text-violet-600" data-testid="revenue-lifetime-value">
                        {formatCents(revenue.lifetime_cents)}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-violet-400" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="revenue-txn-count-card">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Fee Transactions</p>
                      <p className="text-2xl font-bold text-gray-700" data-testid="revenue-txn-count-value">
                        {revenue.transaction_count}
                      </p>
                    </div>
                    <Receipt className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card data-testid="recent-transactions-card">
              <CardHeader>
                <CardTitle className="text-base">Recent Fee Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {txLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : transactions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No fee transactions yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="transactions-table">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-4 font-medium">Date</th>
                          <th className="pb-2 pr-4 font-medium">Tenant</th>
                          <th className="pb-2 pr-4 font-medium">Invoice</th>
                          <th className="pb-2 pr-4 font-medium text-right">Payment</th>
                          <th className="pb-2 font-medium text-right">Platform Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => (
                          <tr key={tx.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`tx-row-${tx.id}`}>
                            <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">
                              {tx.date ? format(new Date(tx.date), 'MMM d, h:mm a') : '—'}
                            </td>
                            <td className="py-2.5 pr-4">
                              <span className="font-medium text-gray-900">{tx.tenant_name}</span>
                            </td>
                            <td className="py-2.5 pr-4">
                              <Badge variant="outline" className="font-mono text-xs">
                                {tx.invoice_number || '—'}
                              </Badge>
                            </td>
                            <td className="py-2.5 pr-4 text-right text-gray-700">
                              {formatCents(tx.amount_cents)}
                            </td>
                            <td className="py-2.5 text-right font-semibold text-emerald-600" data-testid={`tx-fee-${tx.id}`}>
                              {formatCents(tx.fee_cents)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <p className="text-center text-gray-500 py-12">Failed to load revenue data.</p>
        )}
      </div>
    </div>
  );
}
